import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TokenPropertyService } from '../services/token-property.service';
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { SettingsService } from '../services/user/settings.service';
import { TilesService } from '../services/user/tiles.service';
import { BrandingService } from '../services/user/branding.service';

@Component({
  selector: 'app-logoff',
  standalone: true,
  imports: [],
  templateUrl: './logoff.component.html',
  styleUrl: './logoff.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LogoffComponent implements OnInit {
  logoPath: string = null as any;
  clientId: string | null = null;

  constructor(
    private router: Router,
    private tokenPropertyService: TokenPropertyService,
    private cloudWatchRumService: CloudWatchRumService,
    private settingsService: SettingsService,
    private tilesService: TilesService,
    private brandingService: BrandingService
  ) {}

  ngOnInit(): void {
    // Set logo before clearing data
    this.brandingService.getLogoPath().subscribe({
      next: (logo: string) => this.logoPath = logo,
      error: () => this.logoPath = './assets/alight_worklife_logo_black.svg'
      });
    
    // Determine logout source based on referrer or navigation state
    const logoutSource = this.determineLogoutSource();
    
    // Clean up CloudWatch RUM before clearing sessionStorage
    this.cloudWatchRumService.cleanup(logoutSource);
    
    // Clear cached settings
    this.settingsService.clearCache();
    
    // Clear embed URL cache
    this.tilesService.clearEmbedUrlCache();
    
    // Clear authentication-related sessionStorage items specifically
    // instead of clearing everything to avoid CloudWatch RUM errors
    this.clearAuthenticationData();
    
    // Notify TokenPropertyService that tokens have been removed
    this.tokenPropertyService.updateTokenState();
  }
  
  /**
   * Determines the source of the logout for analytics
   * @returns The logout source identifier
   */
  private determineLogoutSource(): string {
    // Check if navigation state contains logout source
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state?.['logoutSource']) {
      return navigation.extras.state['logoutSource'];
    }
    
    // Check if we came from session timeout (already logged in modal)
    // This is a fallback in case the event wasn't captured in the modal
    const currentUrl = this.router.url;
    if (document.referrer.includes('/home') || document.referrer.includes('/admin')) {
      return 'user_initiated';
    }
    
    // Default to direct navigation if no other source identified
    return 'direct_navigation';
  }
  
  private clearAuthenticationData(): void {
    // Clear auth and session keys from sessionStorage (tab-isolated storage)
    const authKeys = [
      'token',
      'refresh_token',
      'id_token',
      'emailId',
      'isInternal',
    ];
    
    authKeys.forEach(key => {
      sessionStorage.removeItem(key);
    });

    // Also clear session-scoped flags
    sessionStorage.removeItem('chat_history');
    sessionStorage.removeItem('chat_session');
    sessionStorage.removeItem('quicksight_registration_complete');
  }
  
  private clearAllExceptRum(): void {
    // Get all localStorage keys
    const allKeys = Object.keys(localStorage);
    
    // CloudWatch RUM typically stores data with keys like:
    // - 'aws-rum-*' or similar patterns
    // We'll preserve any AWS RUM related keys
    const rumKeyPatterns = [
      'aws-rum',
      'cwr-',
      'rum-',
      // Add other patterns if you identify them
    ];
    
    allKeys.forEach(key => {
      // Check if this key should be preserved for CloudWatch RUM
      const shouldPreserve = rumKeyPatterns.some(pattern => 
        key.toLowerCase().includes(pattern.toLowerCase())
      );
      
      if (!shouldPreserve) {
        localStorage.removeItem(key);
      }
    });
  }
}